/**
 * Componente para renderizar mensagens de template WhatsApp no chat
 * 
 * Exibe o nome do template como badge, o texto real da mensagem com variáveis substituídas,
 * e os botões do template (URL, QUICK_REPLY, ORDER_DETAILS) com estilo visual apropriado.
 * Para botões ORDER_DETAILS (PIX), exibe seção especial com dados do pagamento.
 */

import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, MessageSquare, CreditCard, Phone, QrCode, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Interface para botões do template
 */
interface TemplateButton {
  type: string;       // 'URL', 'QUICK_REPLY', 'ORDER_DETAILS', 'PHONE_NUMBER', 'COPY_CODE', etc.
  text: string;       // Texto do botão
  url?: string;       // URL (para botões URL)
  phone_number?: string; // Número de telefone (para botões PHONE_NUMBER)
  code_example?: string; // Exemplo de código (para botões COPY_CODE)
}

/**
 * Interface para parâmetros ORDER_DETAILS (PIX)
 */
interface OrderDetailsParams {
  pix_code: string;
  merchant_name: string;
  pix_key: string;
  pix_key_type: string;
  amount_value: number;
  item_name?: string;
}

/**
 * Interface para parâmetros de botões URL dinâmicos
 */
interface ButtonParam {
  type: 'button';
  sub_type: 'url';
  index: number;
  parameters: Array<{ type: 'text'; text: string }>;
}

interface TemplateMessageBubbleProps {
  /** Nome do template */
  templateName: string;
  /** Texto do body com variáveis já substituídas */
  templateBody: string;
  /** Direção da mensagem */
  direction: 'inbound' | 'outbound';
  /** Botões do template */
  buttons?: TemplateButton[];
  /** Parâmetros para botões URL dinâmicos */
  buttonParams?: ButtonParam[];
  /** Parâmetros PIX para ORDER_DETAILS */
  orderDetailsParams?: OrderDetailsParams;
  /** Formato do header de mídia (IMAGE, VIDEO, DOCUMENT) */
  headerFormat?: string;
  /** URL da mídia do header */
  headerMediaUrl?: string;
}

/**
 * Resolve URL dinâmica substituindo variáveis pelos valores fornecidos
 * @param url - URL com possíveis variáveis {{1}}
 * @param buttonParams - Parâmetros dos botões
 * @param buttonIndex - Índice do botão
 */
function resolveButtonUrl(url: string, buttonParams?: ButtonParam[], buttonIndex?: number): string {
  if (!url || !buttonParams || buttonIndex === undefined) return url;
  
  const param = buttonParams.find(bp => bp.index === buttonIndex);
  if (param?.parameters?.[0]?.text) {
    // Substitui {{1}} pelo valor
    return url.replace(/\{\{1\}\}/, param.parameters[0].text);
  }
  
  return url;
}

/**
 * Retorna o ícone apropriado para o tipo de botão
 */
function getButtonIcon(type: string) {
  switch (type.toUpperCase()) {
    case 'URL':
      return <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />;
    case 'ORDER_DETAILS':
      return <CreditCard className="h-3.5 w-3.5 flex-shrink-0" />;
    case 'QUICK_REPLY':
      return <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />;
    case 'PHONE_NUMBER':
      return <Phone className="h-3.5 w-3.5 flex-shrink-0" />;
    case 'COPY_CODE':
      return <Copy className="h-3.5 w-3.5 flex-shrink-0" />;
    default:
      return null;
  }
}

export function TemplateMessageBubble({
  templateName,
  templateBody,
  direction,
  buttons,
  buttonParams,
  orderDetailsParams,
  headerFormat,
  headerMediaUrl,
}: TemplateMessageBubbleProps) {
  const isOutbound = direction === 'outbound';
  
  return (
    <div className="flex flex-col gap-1.5">
      {/* Badge indicando template - cores fixas para outbound */}
      <Badge 
        variant="outline" 
        className={cn(
          "text-[10px] px-1.5 py-0 w-fit gap-1",
          !isOutbound && "bg-muted text-muted-foreground border-border"
        )}
        style={isOutbound ? {
          backgroundColor: 'hsl(var(--chat-text-primary) / 0.05)',
          color: 'hsl(var(--chat-text-secondary))',
          borderColor: 'hsl(var(--chat-border))'
        } : undefined}
      >
        <FileText className="h-3 w-3" />
        Template: {templateName}
      </Badge>
      
      {/* Header de mídia do template (VIDEO/IMAGE/DOCUMENT) */}
      {headerFormat === 'VIDEO' && headerMediaUrl && (
        <div className="rounded-lg overflow-hidden bg-black/5 mb-1">
          <video src={headerMediaUrl} controls className="w-full max-h-[200px] object-contain" />
        </div>
      )}
      {headerFormat === 'IMAGE' && headerMediaUrl && (
        <div className="rounded-lg overflow-hidden mb-1">
          <img src={headerMediaUrl} alt="Header" className="w-full max-h-[200px] object-cover rounded-lg" />
        </div>
      )}
      {headerFormat === 'DOCUMENT' && headerMediaUrl && (
        <div className={cn(
          "flex items-center gap-2 p-2 rounded-lg mb-1",
          !isOutbound && "bg-muted/50 border border-border"
        )}
        style={isOutbound ? {
          backgroundColor: 'hsl(var(--chat-bubble-inbound))',
          border: '1px solid hsl(var(--chat-border))'
        } : undefined}
        >
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground truncate">Documento anexado</span>
        </div>
      )}

      {/* Texto real da mensagem */}
      <p className="text-sm whitespace-pre-wrap break-all">
        {templateBody}
      </p>

      {/* Botões do template - cores fixas WhatsApp para outbound */}
      {buttons && buttons.length > 0 && (
        <div 
          className="flex flex-col gap-1.5 mt-2 pt-2 border-t"
          style={{ borderTopColor: isOutbound ? 'hsl(var(--chat-border))' : undefined }}
        >
          {buttons.map((btn, idx) => {
            const icon = getButtonIcon(btn.type);
            const resolvedUrl = btn.url 
              ? resolveButtonUrl(btn.url, buttonParams, idx) 
              : undefined;
            
            return (
              <div 
                key={idx} 
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
                  !isOutbound && "bg-muted/50 border border-border"
                )}
                style={isOutbound ? {
                  backgroundColor: 'hsl(var(--chat-bubble-inbound))',
                  border: '1px solid hsl(var(--chat-border))'
                } : undefined}
              >
                {/* Ícone do tipo de botão */}
                {icon && (
                  <span style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined} className={!isOutbound ? "text-muted-foreground" : undefined}>
                    {icon}
                  </span>
                )}
                
                {/* Texto do botão */}
                <span 
                  className="font-medium"
                  style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
                >
                  {btn.text}
                </span>
                
                {/* URL resolvida (para botões URL) */}
                {btn.type.toUpperCase() === 'URL' && resolvedUrl && (
                  <span 
                    className={cn("text-xs truncate ml-auto max-w-[150px]", !isOutbound && "text-muted-foreground")}
                    style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
                  >
                    {resolvedUrl}
                  </span>
                )}
                
                {/* Número de telefone (para botões PHONE_NUMBER) */}
                {btn.type.toUpperCase() === 'PHONE_NUMBER' && btn.phone_number && (
                  <span 
                    className={cn("text-xs ml-auto", !isOutbound && "text-muted-foreground")}
                    style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
                  >
                    {btn.phone_number}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Seção especial para ORDER_DETAILS (PIX) */}
      {orderDetailsParams && orderDetailsParams.pix_code && (
        <div className={cn(
          "mt-2 p-2.5 rounded-lg border",
          isOutbound 
            ? "bg-green-500/10 border-green-500/20" 
            : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
        )}>
          {/* Cabeçalho PIX */}
          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <QrCode className="h-4 w-4" />
            <span className="text-sm font-medium">Pagamento PIX</span>
          </div>
          
          {/* Informações básicas - cores fixas para labels */}
          <div className="mt-1.5 text-xs space-y-0.5">
            <div className="flex justify-between">
              <span style={{ color: 'hsl(var(--chat-text-secondary))' }}>
                Valor:
              </span>
              <span className="font-medium text-green-700 dark:text-green-400">
                R$ {(orderDetailsParams.amount_value / 100).toFixed(2).replace('.', ',')}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'hsl(var(--chat-text-secondary))' }}>
                Recebedor:
              </span>
              <span className="font-medium" style={{ color: 'hsl(var(--chat-text-primary))' }}>{orderDetailsParams.merchant_name}</span>
            </div>
            {orderDetailsParams.item_name && (
              <div className="flex justify-between">
                <span style={{ color: 'hsl(var(--chat-text-secondary))' }}>
                  Descrição:
                </span>
                <span className="font-medium" style={{ color: 'hsl(var(--chat-text-primary))' }}>{orderDetailsParams.item_name}</span>
              </div>
            )}
            {/* Chave PIX com tipo */}
            {orderDetailsParams.pix_key && (
              <div className="flex justify-between">
                <span style={{ color: 'hsl(var(--chat-text-secondary))' }}>
                  Chave PIX:
                </span>
                <span className="font-medium" style={{ color: 'hsl(var(--chat-text-primary))' }}>
                  {orderDetailsParams.pix_key}
                  {orderDetailsParams.pix_key_type && (
                    <span style={{ color: 'hsl(var(--chat-text-secondary))' }} className="ml-1">
                      ({orderDetailsParams.pix_key_type})
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          
          {/* Código PIX copia-cola - destacado para conferência */}
          <div className="mt-2 pt-2 border-t border-green-500/20">
            <div style={{ color: 'hsl(var(--chat-text-secondary))' }} className="text-[10px] mb-1">
              Código PIX Copia-Cola:
            </div>
            <div className={cn(
              "p-2 rounded text-[11px] font-mono break-all select-all cursor-text",
              isOutbound 
                ? "bg-green-500/5 border border-green-500/10" 
                : "bg-green-100/50 dark:bg-green-900/30 border border-green-200 dark:border-green-800"
            )}>
              {orderDetailsParams.pix_code}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
